import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { AdminAccountViewModel } from "../../../api/generated/models/AdminAccountViewModel";
import { AdminAccountsService } from "../../../api/generated/services/AdminAccountsService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface ResendInvitationModalProps {
  isOpen: boolean;
  admin: AdminAccountViewModel | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ResendInvitationModal: React.FC<ResendInvitationModalProps> = ({
  isOpen,
  admin,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!admin?.id) {
      setError("Admin ID is missing");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await AdminAccountsService.postApiAdminAccountsResendAdminAccountInvitationEmail(
        {
          adminAccountId: admin.id,
        }
      );
      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error resending invitation:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to resend invitation. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!admin) {
    return null;
  }

  return (
    <Modal
      className="modal-content"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Resend Invitation"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Resend Invitation</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        {!admin.acceptedInvitation && (
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <p className="text-sm text-orange-800">
              This will invalidate the previously sent invitation. A new
              invitation email will be sent to{" "}
              <span className="font-medium">{admin.email}</span>.
            </p>
          </div>
        )}

        {admin.acceptedInvitation && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              This user has already accepted the invitation by setting up a
              password for this account. We will send them a link to the login
              page and password reset instructions to{" "}
              <span className="font-medium">{admin.email}</span>.
            </p>
          </div>
        )}

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} isLoading={loading}>
            Confirm & Resend
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ResendInvitationModal;
